// frontend/src/pages/admin/reports/ProfitReport.js
import React, { useState, useEffect } from 'react';
import axios from '../../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiTrendingUp,
  FiDollarSign,
  FiPackage,
  FiShoppingCart,
  FiDownload,
  FiPercent
} from 'react-icons/fi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import DateRangePicker from '../../../components/DateRangePicker';

const ProfitReport = () => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [profitByProduct, setProfitByProduct] = useState([]);
  const [profitByCategory, setProfitByCategory] = useState([]);
  const [viewMode, setViewMode] = useState('summary'); // summary, product, category

  const [dateRange, setDateRange] = useState({
    start: new Date().toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  // ✅ FIX: Only fetch when dateRange changes
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch summary
      const summaryRes = await axios.get('/api/reports/profit/summary', {
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end
        }
      });

      if (summaryRes.data.success) {
        setSummary(summaryRes.data.data.summary);
      }

      // Fetch by product
      const productRes = await axios.get('/api/reports/profit/by-product', {
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end,
          limit: 10
        }
      });

      if (productRes.data.success) {
        setProfitByProduct(productRes.data.data.products);
      }

      // Fetch by category
      const categoryRes = await axios.get('/api/reports/profit/by-category', {
        params: {
          start_date: dateRange.start,
          end_date: dateRange.end
        }
      });

      if (categoryRes.data.success) {
        setProfitByCategory(categoryRes.data.data.categories);
      }

    } catch (error) {
      console.error('Fetch profit data error:', error);
      toast.error('Тайлан татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (start, end) => {
    setDateRange({ start, end });
  };

  const exportToExcel = () => {
    toast.info('Excel export функц удахгүй нэмэгдэнэ');
  };

  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner-lg"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Date Range Picker */}
      <DateRangePicker onDateChange={handleDateChange} />

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Total Sales */}
          <div className="card bg-gradient-to-br from-blue-50 to-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-semibold">Нийт борлуулалт</p>
                <p className="text-2xl font-bold text-blue-900">
                  {(parseFloat(summary.total_sales) || 0).toLocaleString()}₮
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  {summary.total_invoices || 0} баримт
                </p>
              </div>
              <FiShoppingCart className="text-blue-600" size={40} />
            </div>
          </div>

          {/* Total Cost */}
          <div className="card bg-gradient-to-br from-orange-50 to-orange-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-semibold">Нийт өртөг</p>
                <p className="text-2xl font-bold text-orange-900">
                  {(parseFloat(summary.total_cost) || 0).toLocaleString()}₮
                </p>
                <p className="text-xs text-orange-600 mt-1">
                  {summary.total_items_sold || 0} бараа
                </p>
              </div>
              <FiPackage className="text-orange-600" size={40} />
            </div>
          </div>

          {/* Net Profit */}
          <div className="card bg-gradient-to-br from-green-50 to-green-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-semibold">Цэвэр ашиг</p>
                <p className="text-2xl font-bold text-green-900">
                  {(parseFloat(summary.net_profit) || 0).toLocaleString()}₮
                </p>
              </div>
              <FiTrendingUp className="text-green-600" size={40} />
            </div>
          </div>

          {/* Profit Margin */}
          <div className="card bg-gradient-to-br from-purple-50 to-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-semibold">Ашгийн хувь</p>
                <p className="text-2xl font-bold text-purple-900">
                  {parseFloat(summary.profit_margin || 0).toFixed(2)}%
                </p>
              </div>
              <FiPercent className="text-purple-600" size={40} />
            </div>
          </div>
        </div>
      )}

      {/* View Mode Toggle */}
      <div className="card mb-6">
        <div className="flex items-center justify-between">
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('product')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                viewMode === 'product'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FiPackage className="inline mr-2" />
              Барааны ашиг
            </button>
            <button
              onClick={() => setViewMode('category')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                viewMode === 'category'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FiDollarSign className="inline mr-2" />
              Ангиллын ашиг
            </button>
          </div>
          <button onClick={exportToExcel} className="btn btn-secondary">
            <FiDownload className="mr-2" />
            Excel татах
          </button>
        </div>
      </div>

      {/* Profit by Product */}
      {viewMode === 'product' && profitByProduct.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bar Chart */}
          <div className="card">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              📊 Топ 10 ашигтай бараа
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={profitByProduct}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="product_name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip formatter={(value) => value.toLocaleString() + '₮'} />
                <Bar dataKey="profit" fill="#10b981" name="Ашиг" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Table */}
          <div className="card">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              📋 Дэлгэрэнгүй
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">№</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Бараа</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Тоо</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Ашиг</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">%</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {profitByProduct.map((product, index) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900">{index + 1}</td>
                      <td className="px-3 py-2">
                        <p className="font-medium text-gray-900">{product.product_name}</p>
                        <p className="text-xs text-gray-500">{product.barcode}</p>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="badge badge-blue">{product.quantity_sold}</span>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-green-600">
                        {parseFloat(product.profit).toLocaleString()}₮
                      </td>
                      <td className="px-3 py-2 text-right text-purple-600 font-medium">
                        {parseFloat(product.profit_margin || 0).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Profit by Category */}
      {viewMode === 'category' && profitByCategory.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie Chart */}
          <div className="card">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              📊 Ангиллаар ашиг
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={profitByCategory}
                  dataKey="profit"
                  nameKey="category_name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ category_name, percent }) => 
                    `${category_name}: ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {profitByCategory.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => value.toLocaleString() + '₮'} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Table */}
          <div className="card">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              📋 Дэлгэрэнгүй
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Ангилал</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Тоо</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Борлуулалт</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Өртөг</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Ашиг</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {profitByCategory.map((category, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <div className="flex items-center">
                          <span 
                            className="w-3 h-3 rounded-full mr-2" 
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          ></span>
                          <span className="font-medium text-gray-900">
                            {category.category_name || 'Бусад'}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="badge badge-blue">{category.quantity_sold}</span>
                      </td>
                      <td className="px-3 py-2 text-right text-blue-600">
                        {parseFloat(category.total_sales).toLocaleString()}₮
                      </td>
                      <td className="px-3 py-2 text-right text-orange-600">
                        {parseFloat(category.total_cost).toLocaleString()}₮
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-green-600">
                        {parseFloat(category.profit).toLocaleString()}₮
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* No Data Messages */}
      {viewMode === 'product' && profitByProduct.length === 0 && (
        <div className="card text-center py-12">
          <FiPackage size={64} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600">Барааны мэдээлэл олдсонгүй</p>
        </div>
      )}

      {viewMode === 'category' && profitByCategory.length === 0 && (
        <div className="card text-center py-12">
          <FiDollarSign size={64} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600">Ангиллын мэдээлэл олдсонгүй</p>
        </div>
      )}
    </div>
  );
};

export default ProfitReport;